import { toast } from "vue3-toastify";
import { i18n } from "@/lang";
import {playlistService} from "@/services/PlaylistService";
import {imageService} from "@/services/ImageService";
import { createDefaultTemplate4Config, normalizeTemplate4Config } from "@/helpers/Template4Config";

const state = {
    playlist: [],
    scene: [],
    playlistsLoaded: false,
    getPlaylistRequest: {
        loading: false
    },
    savePlaylistRequest: {
        loading: false
    },
    saveSceneRequest: {
        loading: false
    },
    deletePlaylistRequest: {
        loading: false
    },
    deleteSceneRequest: {
        loading: false
    },
    currentScene: null,
    currentPlaylist: null,
    configTemplate4: createDefaultTemplate4Config(),
}

const actions = {
    getPlaylist({commit}, orgId){
        commit('setGetPlaylistRequest', {loading: true});
        return playlistService.getPlaylist(orgId)
            .then((response) => {
                commit('updateScene', response?.data?.scenes || []);
                commit('updatePlaylist', response?.data?.playlists || []);
                commit('setPlaylistsLoaded', true);
                commit('setGetPlaylistRequest', {loading: false});
                return true;
            })
            .catch((error) => {
                commit('setPlaylistsLoaded', false);
                commit('setGetPlaylistRequest', {loading: false, message: error || i18n.global.t("playlist.loadError")});
                return false;
            })
    },
    ensurePlaylists({state, dispatch}, orgId) {
        if (state.playlistsLoaded) return Promise.resolve(true);
        return dispatch('getPlaylist', orgId);
    },
    savePlaylist({commit, state}, playlist) {
        if (state.savePlaylistRequest.loading) return Promise.resolve(false);
        commit('setSavePlaylistRequest', {loading: true});
        return playlistService.savePlaylist(playlist)
            .then((response) => {
                if (playlist.playlistId){
                    const updatedPlaylist = state.playlist.map(x => (x.playlistId === playlist.playlistId ? response.data : x))
                    commit('updatePlaylist', updatedPlaylist)
                } else {
                    commit('updatePlaylist', [...state.playlist, response.data])
                }
                commit('setSavePlaylistRequest', {loading: false});
                toast.success(i18n.global.t("toast.successEdit"));
                return true;
            })
            .catch((error) => {
                commit('setSavePlaylistRequest', {loading: false, message: error});
                toast.error(i18n.global.t("toast.errorEdit"));
                return false;
            })
    },
    async saveScene({commit, state}, scene) {
        if (state.saveSceneRequest.loading) return false;
        commit('setSaveSceneRequest', {loading: true});
        const orgId = localStorage.getItem('orgId')
        if (scene.config && typeof scene.config !== 'string') {
            scene.config = JSON.stringify(scene.config);
        }
        if (scene.attachedFile){
            try {
                const file = Array.isArray(scene.attachedFile) ? scene.attachedFile[0] : scene.attachedFile;
                const isVideo = (file?.type || '').startsWith('video/');
                const uploadedUrl = isVideo
                    ? await imageService.uploadImage(file, orgId, 0, 0, false)
                    : await imageService.uploadImage(file, orgId, 200, 1920);
                scene.imageUrl = uploadedUrl;
                scene.backgroundUrl = uploadedUrl;
            } catch (error) {
                commit('setSaveSceneRequest', {loading: false, message: error});
                toast.error(i18n.global.t("toast.errorEdit"));
                return false;
            }
        }
        return playlistService.saveScene(scene)
            .then((response) => {
                if (scene.sceneId){
                    const updatedScenes = state.scene.map(x => (x.sceneId === scene.sceneId ? response.data : x))
                    commit('updateScene', updatedScenes)
                } else {
                    commit('updateScene', [...state.scene, response.data])
                }
                commit('setSaveSceneRequest', {loading: false});
                toast.success(i18n.global.t("toast.successEdit"));
                return true;
            })
            .catch((error) => {
                commit('setSaveSceneRequest', {loading: false, message: error});
                toast.error(i18n.global.t("toast.errorEdit"));
                return false;
            })
    },
    deletePlaylist({commit, state}, playlist) {
        commit('setDeletePlaylistRequest', {loading: true});
        return playlistService.deletePlaylist(playlist)
            .then(() => {
                const updatedPlaylist = state.playlist.filter(x => x.playlistId !== playlist.playlistId)
                commit('updatePlaylist', updatedPlaylist)
                commit('setDeletePlaylistRequest', {loading: false});
                toast.success(i18n.global.t("toast.deleteSuccess"));
            })
            .catch((error) => {
                commit('setDeletePlaylistRequest', {loading: false, message: error});
                toast.error(i18n.global.t("toast.deleteError"));
            })
    },
    deleteScene({commit, state}, scene) {
        commit('setDeleteSceneRequest', {loading: true});
        return playlistService.deleteScene(scene)
            .then(() => {
                const updatedScene = state.scene.filter(x => x.sceneId !== scene.sceneId)
                commit('updateScene', updatedScene)
                commit('setDeleteSceneRequest', {loading: false});
                toast.success(i18n.global.t("toast.deleteSuccess"));
            })
            .catch((error) => {
                commit('setDeleteSceneRequest', {loading: false, message: error});
                toast.error(i18n.global.t("toast.deleteError"));
            })
    }
}

const mutations = {
    setPlaylistsLoaded(state, loaded) {
        state.playlistsLoaded = loaded;
    },
    setGetPlaylistRequest(state, {loading, message}) {
        state.getPlaylistRequest = {loading, message};
    },
    setSavePlaylistRequest(state, {loading, message}) {
        state.savePlaylistRequest = {loading, message};
    },
    setSaveSceneRequest(state, {loading, message}) {
        state.saveSceneRequest = {loading, message};
    },
    setDeletePlaylistRequest(state, {loading, message}) {
        state.deletePlaylistRequest = {loading, message};
    },
    setDeleteSceneRequest(state, {loading, message}) {
        state.deleteSceneRequest = {loading, message};
    },
    updatePlaylist(state, newList){
        state.playlist = newList;
    },
    updateScene(state, newList) {
        state.scene = newList;
    },
    setCurrentScene(state, scene) {
        state.currentScene = scene;

        let parsedConfig = {};

        if (scene.config) {
            try {
                parsedConfig = typeof scene.config === 'string'
                    ? JSON.parse(scene.config)
                    : scene.config;
                console.log(parsedConfig)
            } catch (e) {
                console.error('Erreur de parsing du config de la scène :', e);
                parsedConfig = {};
            }
        }

        if (scene.templateName === 'posbel_4') {
            state.configTemplate4 = normalizeTemplate4Config(parsedConfig);
        }
    },
    setCurrentPlaylist(state, playlist) {
        state.currentPlaylist = playlist
    },
    setCurrentConfigTemplate4(state, config) {
        state.configTemplate4 = normalizeTemplate4Config(config)
    },
}

export const playlist = {
    namespaced: true,
    state,
    actions,
    mutations,
}
